/**
 * Calendar Section Component for SearchBarInput
 * 
 * Displays the current/selected date and opens calendar modal on click.
 * Only used for arrival input type.
 * 
 * @module CalendarSection
 */

import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react";
import { ARRIVAL_ICON } from "../../config/CalendarIcons";
import SearchBarDepartureIcon from "../../image/icons/searchbar/SearchBarDepartureIcon.svg?react";
import SearchBarRefreshIcon from "../../image/icons/searchbar/SearchBarRefreshIcon.svg?react";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { timeToString } from "../../tools/TimeRelated";
import { type DateWithMode } from "../../types/Forms";
import MuiCalendar from "../Calendar/MuiCalendar";

interface CalendarSectionProps {

    /** Current or selected date (timestamp in milliseconds) */
    "currentOrSelectedDate": number;

    /** Date value from props with mode information */
    "date": DateWithMode;

    /** Whether date has been manually edited */
    "isEditedDate": boolean;

    /** Setter for date */
    "setDate": Dispatch<SetStateAction<DateWithMode>>;

    /** Setter for edited date flag */
    "setIsEditedDate": Dispatch<SetStateAction<boolean>>;

    /** Whether modal is open */
    "isModalOpen": boolean;

    /** Setter for modal open state */
    "setIsModalOpen": Dispatch<SetStateAction<boolean>>;

    /** Whether to show refresh button */
    "shouldShowRefreshButton": boolean;

    /** Callback to refresh date to current time */
    "onRefreshDate": () => void;

    /** Whether dark UI palette is active */
    "isDarkUi": boolean;
}

/**
 * Calendar Section Component
 * 
 * @param {CalendarSectionProps} props - Component props
 * @returns {JSX.Element} Calendar section component
 */
const CalendarSection: React.FC<CalendarSectionProps> = ( {
    currentOrSelectedDate,
    date,
    setIsEditedDate,
    isModalOpen,
    setIsModalOpen,
    setDate,
    shouldShowRefreshButton,
    onRefreshDate,
    isDarkUi,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const handleCalendar = useCallback( () => {
        setIsModalOpen( true );
    }, [ setIsModalOpen ] );

    const closeModal = useCallback( () => {
        setIsModalOpen( false );
    }, [ setIsModalOpen ] );

    const handleRefreshClick = useCallback( ( e: React.MouseEvent ) => {
        e.stopPropagation();
        onRefreshDate();
    }, [ onRefreshDate ] );

    const dateObj = new Date( currentOrSelectedDate );
    const day = dateObj.getDate().toString().
        padStart( 2, "0" );
    const month = ( dateObj.getMonth() + 1 ).toString().
        padStart( 2, "0" );
    const year = dateObj.getFullYear();
    const dateStr = `${ day }.${ month }.${ year.toString().slice( -2 ) }`;
    const timeStr = timeToString( currentOrSelectedDate );

    // Check if the date is different from today
    const today = new Date();
    const isDifferentFromToday =
        dateObj.getDate() !== today.getDate() ||
        dateObj.getMonth() !== today.getMonth() ||
        dateObj.getFullYear() !== today.getFullYear();

    return (
        <div
            className="absolute w-full md:w-3/5 top-0"
            onClick={handleCalendar}
        >
            <div className="absolute space-between top-3 z-40 h-9 md:right-0 right-3 text-sm cursor-pointer flex items-center gap-0 px-1 py-1 font-bold bg-emerald-400 bg-opacity-50 backdrop-blur-sm rounded-xl rounded-b-none transition-all duration-200 w-[150px]">
                <div className="flex items-center justify-center w-6 h-6 rounded-lg">
                    <span className="text-lg">
                        {date.isDepartureTimestamp && (
                            <SearchBarDepartureIcon
                                aria-label="Departure"
                                className="w-5 h-5 inline-block align-middle"
                                style={{ "color": isDarkUi ? palette.overlayTextPrimary : "#44403c" }}
                            />
                        )}
                        {!date.isDepartureTimestamp && ARRIVAL_ICON}
                    </span>
                </div>
                <div className="flex flex-row grow items-center justify-center gap-1">
                    {isDifferentFromToday && (
                        <>
                            <span
                                className="text-sm font-bold"
                                style={isDarkUi ? { "color": palette.overlayTextPrimary } : undefined}
                            >
                                {dateStr}
                            </span>
                            <span className="text-xs text-stone-400">
                                •
                            </span>
                        </>
                    )}
                    <span
                        className="text-sm font-semibold"
                        style={isDarkUi ? { "color": palette.overlayTextPrimary } : undefined}
                    >
                        {timeStr}
                    </span>
                </div>
                {shouldShowRefreshButton && (
                    <button
                        className="flex items-center justify-center w-5 h-5 rounded-lg transition-colors"
                        onClick={handleRefreshClick}
                        title="Rafraîchir à l'heure actuelle"
                        type="button"
                    >
                        <SearchBarRefreshIcon
                            aria-label="Rafraîchir à l'heure actuelle"
                            className="w-5 h-5 inline-block align-middle"
                            style={{ "color": isDarkUi ? palette.overlayTextPrimary : "#44403c" }}
                        />
                    </button>
                )}
            </div>
            <MuiCalendar
                date={date}
                isDarkUi={isDarkUi}
                isOpen={isModalOpen}
                onClose={closeModal}
                setDate={setDate}
                setIsEditedDate={setIsEditedDate}
            />
        </div>
    );
};

export default CalendarSection;

